import { TableBody, TableHead, TableRow } from '@mui/material';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from 'styled-components';

import { Case } from '../../../../shared/api/hscan/case';
import {
  MobileTable,
  MobileTableRow,
} from '../../common/components/MobileTable';
import {
  HscanTable,
  HscanTableCell,
} from '../../common/components/SortableTable';
import {
  createTableElementInformationContext,
  createTableInformationContext,
  PresentationalTableProps,
  TableAndElementInformation,
} from '../../common/components/SortableTable/PresentationalTable';
import { ElementWrapper } from '../../common/components/SortableTable/RenderTable';
import { useModalityLabel } from '../../hooks/modalityHook';
import { WindowSize, WindowSizeContext } from '../../hooks/windowSizeHook';

export const { TableContext, TableInformationProvider } =
  createTableInformationContext<Case, 'caseId'>();
export const { TableElementContext, TableElementInformationProvider } =
  createTableElementInformationContext<Case, 'caseId'>(TableContext);

type RequestCdTableProps = Omit<
  PresentationalTableProps<Case, 'caseId'>,
  'identifyKey'
>;
export const RequestCdTable = (props: RequestCdTableProps) => {
  const isMobile = useContext(WindowSizeContext) === WindowSize.MOBILE;

  return (
    <TableInformationProvider {...props} identifyKey="caseId">
      {isMobile ? <RequestMobileCdTable /> : <RequestWebCdTable />}
    </TableInformationProvider>
  );
};

export const RequestWebCdTable = () => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'features.imageList.cd.request.table',
  });
  const { data } = useContext(TableContext);
  const modalityLabel = useModalityLabel();
  return (
    <HscanTable
      head={
        <TableHead>
          <TableRow>
            <HscanTableCell>{t('institutionName')}</HscanTableCell>
            <HscanTableCell>{t('studyDate')}</HscanTableCell>
            <HscanTableCell>{t('modality')}</HscanTableCell>
            <HscanTableCell>{t('bodyPart')}</HscanTableCell>
          </TableRow>
        </TableHead>
      }
      body={
        <TableBody>
          {data.map(caseInfo => (
            <TableRow key={caseInfo.caseId}>
              <HscanTableCell>{caseInfo.institutionName ?? ''}</HscanTableCell>
              <HscanTableCell>{caseInfo.studyDate}</HscanTableCell>
              <HscanTableCell>
                {modalityLabel(caseInfo.modality)}
              </HscanTableCell>
              <HscanTableCell>
                {caseInfo.bodyPart?.join(', ') ?? ''}
              </HscanTableCell>
            </TableRow>
          ))}
        </TableBody>
      }
    />
  );
};

const TableContainer = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 20px;
  min-height: 326px;
  padding: 5px;
  overflow: auto;
`;
export const RequestMobileCdTable = () => {
  const { data, identifyKey } = useContext(TableContext);
  return (
    <TableContainer>
      {data.map((element, elementIndex) => (
        <TableElementInformationProvider
          key={`${element[identifyKey]}`}
          element={element}
          elementIndex={elementIndex}>
          <ElementWrapper
            tableContext={TableContext}
            elementContext={TableElementContext}
            Element={MobileCdTableElement}
          />
        </TableElementInformationProvider>
      ))}
    </TableContainer>
  );
};

const ElementContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
`;
const MobileCdTableElement = (
  props: TableAndElementInformation<Case, 'caseId'>,
) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'features.imageList.cd.request.table',
  });
  const modalityLabel = useModalityLabel();
  const { element } = props;

  return (
    <ElementContainer>
      <MobileTable>
        <tbody>
          <MobileTableRow label={t('institutionName')}>
            {element.institutionName ?? ''}
          </MobileTableRow>
          <MobileTableRow label={t('studyDate')}>
            {element.studyDate ?? ''}
          </MobileTableRow>
          <MobileTableRow label={t('modality')}>
            {modalityLabel(element.modality)}
          </MobileTableRow>
          <MobileTableRow label={t('bodyPart')}>
            {element.bodyPart?.join(', ') ?? ''}
          </MobileTableRow>
        </tbody>
      </MobileTable>
    </ElementContainer>
  );
};
